import React from 'react';

/* ━━━ 1. Network Graph ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface NetworkGraphProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const NetworkGraph: React.FC<NetworkGraphProps> = ({
  highlightTarget, labels = ['Node A','Node B','Node C','Node D','Node E'],
  accentColor = '#8b5cf6', opacity = 1,
}) => {
  const nodes = [
    { x: 400, y: 140 }, { x: 260, y: 240 }, { x: 540, y: 240 },
    { x: 310, y: 380 }, { x: 490, y: 380 },
  ];
  const edges = [[0,1],[0,2],[1,2],[1,3],[2,4],[3,4]];
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="ng-nd" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <filter id="ng-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.4"/></filter>
      </defs>
      {/* Edges */}
      {edges.map(([a, b], i) => {
        const na = nodes[a], nb = nodes[b];
        return <g key={`e${i}`}>
          <line x1={na.x+2} y1={na.y+3} x2={nb.x+2} y2={nb.y+3} stroke="rgba(0,0,0,0.25)" strokeWidth="3"/>
          <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y} stroke={accentColor} strokeWidth="2" opacity="0.35"/>
          <line x1={na.x} y1={na.y} x2={nb.x} y2={nb.y} stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
        </g>;
      })}
      {/* Nodes */}
      {nodes.slice(0, labels.length).map((n, i) => {
        const id = `node_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#ng-sh)">
          <circle cx={n.x+3} cy={n.y+4} r="24" fill="rgba(0,0,0,0.3)"/>
          <circle cx={n.x} cy={n.y} r="24" fill="url(#ng-nd)" stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}/>
          <circle cx={n.x-7} cy={n.y-8} r="5" fill="rgba(255,255,255,0.4)"/>
          <text x={n.x} y={n.y+40} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontFamily="Inter,sans-serif" fontWeight="500">{labels[i]}</text>
          {hi && <circle cx={n.x} cy={n.y} r="32" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 2. Circuit Board ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface CircuitBoardProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const CircuitBoard: React.FC<CircuitBoardProps> = ({
  highlightTarget, labels = ['Input','Resistor','Capacitor','IC','Output'],
  accentColor = '#22c55e', opacity = 1,
}) => {
  const components = [
    { x: 120, y: 260, w: 70, h: 40 }, { x: 240, y: 180, w: 70, h: 40 },
    { x: 370, y: 260, w: 70, h: 40 }, { x: 500, y: 180, w: 80, h: 50 },
    { x: 640, y: 260, w: 70, h: 40 },
  ];
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="cb-comp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#334155"/><stop offset="100%" stopColor="#1e293b"/></linearGradient>
        <filter id="cb-sh"><feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.35"/></filter>
      </defs>
      {/* PCB background */}
      <rect x="80" y="100" width="640" height="300" rx="8" fill="#0c2818" stroke={accentColor} strokeWidth="0.5" opacity="0.3"/>
      {/* Traces */}
      {components.map((c, i) => {
        if (i >= components.length - 1) return null;
        const nc = components[i + 1];
        const fromX = c.x + c.w, fromY = c.y + c.h / 2;
        const toX = nc.x, toY = nc.y + nc.h / 2;
        const midX = (fromX + toX) / 2;
        return <g key={`t${i}`}>
          <path d={`M${fromX},${fromY} L${midX},${fromY} L${midX},${toY} L${toX},${toY}`}
            fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="4" transform="translate(1,2)"/>
          <path d={`M${fromX},${fromY} L${midX},${fromY} L${midX},${toY} L${toX},${toY}`}
            fill="none" stroke={accentColor} strokeWidth="3" opacity="0.5" strokeLinejoin="round"/>
          <path d={`M${fromX},${fromY} L${midX},${fromY} L${midX},${toY} L${toX},${toY}`}
            fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
        </g>;
      })}
      {/* Components */}
      {components.slice(0, labels.length).map((c, i) => {
        const id = `comp_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#cb-sh)">
          {/* 3D offset */}
          <rect x={c.x+3} y={c.y+3} width={c.w} height={c.h} rx="3" fill="rgba(0,0,0,0.3)"/>
          <rect x={c.x} y={c.y} width={c.w} height={c.h} rx="3" fill="url(#cb-comp)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2:1}/>
          <line x1={c.x+3} y1={c.y+2} x2={c.x+c.w-3} y2={c.y+2} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
          {/* Pins */}
          <rect x={c.x+c.w/2-2} y={c.y-6} width="4" height="6" fill="#94a3b8" rx="1"/>
          <rect x={c.x+c.w/2-2} y={c.y+c.h} width="4" height="6" fill="#94a3b8" rx="1"/>
          <text x={c.x + c.w/2} y={c.y + c.h/2 + 4} textAnchor="middle" fill="#e2e8f0" fontSize="9" fontFamily="Inter,sans-serif" fontWeight="600">{labels[i]}</text>
          {hi && <rect x={c.x-4} y={c.y-4} width={c.w+8} height={c.h+8} rx="5" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></rect>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 3. Neural Network ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface NeuralNetworkProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  layers?: number[];
}
export const NeuralNetwork: React.FC<NeuralNetworkProps> = ({
  highlightTarget, labels = ['Input','Hidden 1','Hidden 2','Output'],
  accentColor = '#a855f7', opacity = 1, layers = [3, 5, 5, 2],
}) => {
  const startX = 140, endX = 660, topY = 80, botY = 420;
  const nLayers = layers.length;
  const layerX = layers.map((_, i) => startX + i * ((endX - startX) / (nLayers - 1)));
  const nodePos: { x: number; y: number; layer: number; idx: number }[] = [];
  layers.forEach((count, li) => {
    const gap = (botY - topY) / (count + 1);
    for (let ni = 0; ni < count; ni++) {
      nodePos.push({ x: layerX[li], y: topY + gap * (ni + 1), layer: li, idx: ni });
    }
  });
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="nn-nd" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <filter id="nn-sh"><feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.3"/></filter>
      </defs>
      {/* Connections between adjacent layers */}
      {nodePos.map((n) => {
        if (n.layer >= nLayers - 1) return null;
        const nextLayer = nodePos.filter(p => p.layer === n.layer + 1);
        return nextLayer.map((m) => <line key={`c${n.layer}${n.idx}-${m.idx}`}
          x1={n.x} y1={n.y} x2={m.x} y2={m.y} stroke={accentColor} strokeWidth="0.8" opacity="0.15"/>
        );
      })}
      {/* Nodes */}
      {nodePos.map((n, i) => {
        const id = `neuron_${n.layer}_${n.idx}`;
        const hi = highlightTarget === `layer_${n.layer+1}`;
        return <g key={i} filter="url(#nn-sh)">
          <circle cx={n.x+1} cy={n.y+2} r="10" fill="rgba(0,0,0,0.25)"/>
          <circle cx={n.x} cy={n.y} r="10" fill="url(#nn-nd)" opacity={hi?1:0.7} stroke={hi?'#fff':'none'} strokeWidth="1.5"/>
          <circle cx={n.x-3} cy={n.y-3} r="2" fill="rgba(255,255,255,0.4)"/>
        </g>;
      })}
      {/* Layer labels */}
      {labels.slice(0, nLayers).map((label, i) => (
        <text key={`l${i}`} x={layerX[i]} y={botY + 20} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Inter,sans-serif" fontWeight="500">{label}</text>
      ))}
    </g>
  );
};

/* ━━━ 4. Block Chain ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface BlockChainProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const BlockChain: React.FC<BlockChainProps> = ({
  highlightTarget, labels = ['Block 0','Block 1','Block 2','Block 3'],
  accentColor = '#f59e0b', opacity = 1,
}) => {
  const n = labels.length, blockW = 110, blockH = 80, gap = 40;
  const totalW = n * blockW + (n - 1) * gap;
  const startX = 400 - totalW / 2, cy = 250;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="bc-blk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#334155"/><stop offset="100%" stopColor="#1e293b"/></linearGradient>
        <filter id="bc-sh"><feDropShadow dx="3" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {labels.map((label, i) => {
        const x = startX + i * (blockW + gap);
        const y = cy - blockH / 2;
        const id = `block_${i}`;
        const hi = highlightTarget === id;
        return <g key={i}>
          {/* Chain link */}
          {i > 0 && <>
            <line x1={x-gap+2} y1={cy+2} x2={x-2} y2={cy+2} stroke="rgba(0,0,0,0.3)" strokeWidth="4"/>
            <line x1={x-gap} y1={cy} x2={x} y2={cy} stroke={accentColor} strokeWidth="3" opacity="0.5"/>
            <circle cx={x - gap/2} cy={cy} r="5" fill={accentColor} opacity="0.6"/>
          </>}
          {/* 3D top face */}
          <path d={`M${x},${y} L${x+blockW},${y} L${x+blockW+8},${y-6} L${x+8},${y-6}Z`} fill={accentColor} opacity="0.2"/>
          {/* 3D right face */}
          <path d={`M${x+blockW},${y} L${x+blockW},${y+blockH} L${x+blockW+8},${y+blockH-6} L${x+blockW+8},${y-6}Z`} fill="rgba(0,0,0,0.25)"/>
          {/* Front face */}
          <rect x={x} y={y} width={blockW} height={blockH} rx="4" fill="url(#bc-blk)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2.5:1} filter="url(#bc-sh)"/>
          <line x1={x+4} y1={y+3} x2={x+blockW-4} y2={y+3} stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
          {/* Hash preview */}
          <text x={x+blockW/2} y={y+20} textAnchor="middle" fill="#94a3b8" fontSize="8" fontFamily="monospace">#{(i*2741).toString(16).slice(0,6)}</text>
          <text x={x+blockW/2} y={y+blockH/2+8} textAnchor="middle" fill="#e2e8f0" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="600">{label}</text>
          {hi && <rect x={x-4} y={y-4} width={blockW+8} height={blockH+8} rx="6" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></rect>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 5. Server Rack ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ServerRackProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const ServerRack: React.FC<ServerRackProps> = ({
  highlightTarget, labels = ['Load Balancer','App Server','Cache','Database'],
  accentColor = '#0ea5e9', opacity = 1,
}) => {
  const cx = 400, startY = 70, unitH = 60, unitW = 340, gap = 12;
  const n = labels.length;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="sr-unit" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#334155"/><stop offset="100%" stopColor="#1e293b"/></linearGradient>
        <filter id="sr-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Rack frame */}
      <rect x={cx-unitW/2-16} y={startY-14} width={unitW+32} height={n*(unitH+gap)+20} rx="6"
        fill="#0f172a" stroke="#334155" strokeWidth="2"/>
      {labels.map((label, i) => {
        const x = cx - unitW / 2, y = startY + i * (unitH + gap);
        const id = `server_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#sr-sh)">
          {/* 3D depth */}
          <rect x={x+3} y={y+3} width={unitW} height={unitH} rx="4" fill="rgba(0,0,0,0.3)"/>
          {/* Unit */}
          <rect x={x} y={y} width={unitW} height={unitH} rx="4" fill="url(#sr-unit)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2:0.8}/>
          <line x1={x+4} y1={y+3} x2={x+unitW-4} y2={y+3} stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>
          {/* LED indicators */}
          <circle cx={x+18} cy={y+unitH/2} r="4" fill={hi?'#22c55e':'#475569'}/>
          <circle cx={x+32} cy={y+unitH/2} r="4" fill={accentColor} opacity="0.5"/>
          {/* Vent lines */}
          {Array.from({length: 5}, (_, j) => (
            <line key={j} x1={x+unitW-60+j*10} y1={y+12} x2={x+unitW-60+j*10} y2={y+unitH-12} stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          ))}
          <text x={x+60} y={y+unitH/2+4} fill="#e2e8f0" fontSize="12" fontFamily="Inter,sans-serif" fontWeight="500">{label}</text>
          {hi && <rect x={x-4} y={y-4} width={unitW+8} height={unitH+8} rx="6" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></rect>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 6. Flow Chart ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface FlowChartProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const FlowChart: React.FC<FlowChartProps> = ({
  highlightTarget, labels = ['Start','Check','Process A','Process B','End'],
  accentColor = '#6366f1', opacity = 1,
}) => {
  // Simple layout: Start (round) → Decision (diamond) → two rects → End (round)
  const shapes: { x: number; y: number; type: 'rect' | 'diamond' | 'round'; label: string; id: string }[] = [
    { x: 400, y: 80, type: 'round', label: labels[0] || 'Start', id: 'shape_1' },
    { x: 400, y: 200, type: 'diamond', label: labels[1] || 'Check', id: 'shape_2' },
    { x: 260, y: 330, type: 'rect', label: labels[2] || 'A', id: 'shape_3' },
    { x: 540, y: 330, type: 'rect', label: labels[3] || 'B', id: 'shape_4' },
    { x: 400, y: 430, type: 'round', label: labels[4] || 'End', id: 'shape_5' },
  ];
  const arrows: [number, number, string?][] = [[0,1],[1,2,'Yes'],[1,3,'No'],[2,4],[3,4]];
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="fc-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e293b"/><stop offset="100%" stopColor="#0f172a"/></linearGradient>
        <marker id="fc-arr" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8Z" fill={accentColor}/></marker>
        <filter id="fc-sh"><feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.35"/></filter>
      </defs>
      {/* Arrows */}
      {arrows.map(([from, to, lbl], i) => {
        const a = shapes[from], b = shapes[to];
        return <g key={`a${i}`}>
          <line x1={a.x} y1={a.y + 30} x2={b.x} y2={b.y - 30} stroke={accentColor} strokeWidth="2" opacity="0.5" markerEnd="url(#fc-arr)"/>
          {lbl && <text x={(a.x+b.x)/2 + (b.x > a.x ? 8 : -8)} y={(a.y+b.y)/2+5}
            fill="#94a3b8" fontSize="9" fontFamily="Inter,sans-serif" fontWeight="600">{lbl}</text>}
        </g>;
      })}
      {/* Shapes */}
      {shapes.map((s) => {
        const hi = highlightTarget === s.id;
        if (s.type === 'round') return <g key={s.id} filter="url(#fc-sh)">
          <ellipse cx={s.x+2} cy={s.y+3} rx="44" ry="24" fill="rgba(0,0,0,0.3)"/>
          <ellipse cx={s.x} cy={s.y} rx="44" ry="24" fill="url(#fc-bg)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2.5:1.5}/>
          <text x={s.x} y={s.y+4} textAnchor="middle" fill="#e2e8f0" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="600">{s.label}</text>
        </g>;
        if (s.type === 'diamond') return <g key={s.id} filter="url(#fc-sh)">
          <path d={`M${s.x},${s.y-32} L${s.x+50},${s.y} L${s.x},${s.y+32} L${s.x-50},${s.y}Z`} fill="rgba(0,0,0,0.3)" transform="translate(2,3)"/>
          <path d={`M${s.x},${s.y-30} L${s.x+48},${s.y} L${s.x},${s.y+30} L${s.x-48},${s.y}Z`} fill="url(#fc-bg)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2.5:1.5}/>
          <text x={s.x} y={s.y+4} textAnchor="middle" fill="#e2e8f0" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="600">{s.label}</text>
        </g>;
        return <g key={s.id} filter="url(#fc-sh)">
          <rect x={s.x-48+2} y={s.y-22+3} width="96" height="44" rx="4" fill="rgba(0,0,0,0.3)"/>
          <rect x={s.x-48} y={s.y-22} width="96" height="44" rx="4" fill="url(#fc-bg)" stroke={hi?'#fff':accentColor} strokeWidth={hi?2.5:1.5}/>
          <line x1={s.x-44} y1={s.y-20} x2={s.x+44} y2={s.y-20} stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
          <text x={s.x} y={s.y+4} textAnchor="middle" fill="#e2e8f0" fontSize="11" fontFamily="Inter,sans-serif" fontWeight="600">{s.label}</text>
        </g>;
      })}
    </g>
  );
};
